/**
 * .tuMetrics {"tu1": 0.86, "tu2": 0.88, "tu3": 6, "notes": "Help agent context: open/close, search, tours, analytics tracking", "tuVersion": "2.5"}
 * .help "React context for the Living Help System: Max agent gutter, element help, tours, and analytics."
 */
// =====================================================
// HELP CONTEXT - React Context Provider
// =====================================================
// Central state management for the Living Help System
// Provides: agent state, element interaction, help content, tours

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type {
  HelpContextValue,
  HelpContext as HelpContextType,
  HelpComponent,
  HelpFAQ,
  HelpSearchResult,
  HelpTourProgress,
  HelpAnalytics,
  HelpFeedback,
} from '../types/help';
import * as helpService from '../services/helpService';
import useUserStore from '@/store/userStore';

// =====================================================
// Context Creation
// =====================================================

const HelpContext = createContext<HelpContextValue | null>(null);

// =====================================================
// Helper Functions
// =====================================================

/**
 * Highlight an element on the page with spotlight effect
 */
function highlightElementById(dataHelpId: string, duration = 3000): void {
  const element = document.querySelector(`[data-help-id="${dataHelpId}"]`);
  
  if (!element) {
    console.warn(`Element with data-help-id="${dataHelpId}" not found`);
    return;
  }

  // Add highlight class
  element.classList.add('help-highlight');
  
  // Scroll into view
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });

  // Remove highlight after duration
  setTimeout(() => {
    element.classList.remove('help-highlight');
  }, duration);
}

/**
 * Scroll to an element smoothly
 */
function scrollToElementById(dataHelpId: string): void {
  const element = document.querySelector(`[data-help-id="${dataHelpId}"]`);
  
  if (!element) {
    console.warn(`Element with data-help-id="${dataHelpId}" not found`);
    return;
  }

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}

// =====================================================
// Provider Component
// =====================================================

interface HelpProviderProps {
  children: React.ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const currentUser = useUserStore(state => state.currentUser);

  // ===================================================
  // State
  // ===================================================

  // Agent state
  const [showAgent, setShowAgent] = useState(false);
  const [, setInitialQuestion] = useState<string | undefined>();
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null); // Track which agent is active in gutter

  // Current context (where user is in app)
  const [currentContext, setCurrentContext] = useState<HelpContextType>({
    page: window.location.pathname,
  });

  // Help content cache
  const [helpComponents, setHelpComponents] = useState<HelpComponent[]>([]);
  const [faqs, setFAQs] = useState<HelpFAQ[]>([]);
  const [, setIsLoading] = useState(true);

  // ===================================================
  // Load Help Index on Mount
  // ===================================================

  useEffect(() => {
    async function loadHelpIndex() {
      setIsLoading(true);
      
      try {
        const [components, allFaqs] = await Promise.all([
          helpService.loadHelpComponents(),
          helpService.getAllFAQs(),
        ]);

        setHelpComponents(components);
        setFAQs(allFaqs);
      } catch (error) {
        console.error('Error loading help index:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadHelpIndex();
  }, []);

  // ===================================================
  // Update Context on Route Change
  // ===================================================

  useEffect(() => {
    setCurrentContext({
      page: window.location.pathname,
    });
  }, [window.location.pathname]);

  // ===================================================
  // Agent Methods
  // ===================================================

  const openAgent = useCallback((question?: string, agentId?: string) => {
    setInitialQuestion(question);
    setShowAgent(true);
    // If agentId provided, set it; otherwise keep current or default to null
    if (agentId) {
      setActiveAgentId(agentId);
    }
  }, []);

  const closeAgent = useCallback(() => {
    setShowAgent(false);
    setInitialQuestion(undefined);
    // Keep activeAgentId so it persists when reopening
  }, []);

  // ===================================================
  // Element Interaction Methods
  // ===================================================

  const highlightElement = useCallback((dataHelpId: string, duration = 3000) => {
    highlightElementById(dataHelpId, duration);
  }, []);

  const scrollToElement = useCallback((dataHelpId: string) => {
    scrollToElementById(dataHelpId);
  }, []);

  // ===================================================
  // Help Content Methods
  // ===================================================

  const searchHelp = useCallback(async (query: string): Promise<HelpSearchResult[]> => {
    return await helpService.searchHelp(query);
  }, []);

  const getComponentHelp = useCallback(async (componentId: string): Promise<HelpComponent | null> => {
    // Check cache first
    const cached = helpComponents.find(c => c.componentId === componentId);
    if (cached) return cached;

    // Load from database
    return await helpService.getHelpComponent(componentId);
  }, [helpComponents]);

  const getFAQ = useCallback(async (faqId: string): Promise<HelpFAQ | null> => {
    // Check cache first
    const cached = faqs.find(f => f.id === faqId);
    if (cached) return cached;

    // Load from database
    return await helpService.getFAQ(faqId);
  }, [faqs]);

  const getLatestVersion = useCallback(async () => {
    return await helpService.getLatestVersion();
  }, []);

  // ===================================================
  // Tour Methods
  // ===================================================

  const startTour = useCallback((tourId: string) => {
    // TODO: Implement tour functionality in Phase 4
    console.log('Starting tour:', tourId);
  }, []);

  const getTourProgress = useCallback(async (tourId: string): Promise<HelpTourProgress | null> => {
    if (!currentUser?.id) return null;
    return await helpService.getTourProgress(currentUser.id, tourId);
  }, [currentUser]);

  // ===================================================
  // Analytics Methods
  // ===================================================

  const trackHelpInteraction = useCallback(async (interaction: Partial<HelpAnalytics>) => {
    await helpService.trackHelpInteraction({
      ...interaction,
      userId: currentUser?.id,
      context: {
        ...currentContext,
        ...interaction.context,
      },
    });
  }, [currentUser, currentContext]);

  const submitFeedback = useCallback(async (feedback: Omit<HelpFeedback, 'id' | 'createdAt' | 'updatedAt'>) => {
    await helpService.submitFeedback({
      ...feedback,
      userId: currentUser?.id,
    });
  }, [currentUser]);

  // ===================================================
  // Agent Switching (after analytics methods)
  // ===================================================

  const switchAgent = useCallback((agentId: string) => {
    setActiveAgentId(agentId);
    // Track agent switch for analytics (provide required answer_source field)
    trackHelpInteraction({
      answerSource: 'agent_selector', // Required field for help_analytics table
      context: {
        interactionType: 'agent_switch',
        targetAgentId: agentId,
      },
    });
  }, [trackHelpInteraction]);

  // ===================================================
  // Context Value
  // ===================================================

  const value = useMemo<HelpContextValue>(() => ({
    // Agent state
    showAgent,
    openAgent,
    closeAgent,
    activeAgentId,
    switchAgent,

    // Element interaction
    highlightElement,
    scrollToElement,

    // Help content
    helpComponents,
    faqs,
    searchHelp,
    getComponentHelp,
    getFAQ,
    getLatestVersion,

    // Tours
    startTour,
    getTourProgress,

    // Context awareness
    currentContext,
    setCurrentContext,

    // Analytics
    trackHelpInteraction,
    submitFeedback,
  }), [
    showAgent,
    openAgent,
    closeAgent,
    activeAgentId,
    switchAgent,
    highlightElement,
    scrollToElement,
    helpComponents,
    faqs,
    searchHelp,
    getComponentHelp,
    getFAQ,
    getLatestVersion,
    startTour,
    getTourProgress,
    currentContext,
    trackHelpInteraction,
    submitFeedback,
  ]);

  // ===================================================
  // Render
  // ===================================================

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

// =====================================================
// Default / fallback value when used outside HelpProvider
// Prevents crash during router error recovery or re-renders
// =====================================================

const defaultHelpContextValue: HelpContextValue = {
  showAgent: false,
  openAgent: () => {},
  closeAgent: () => {},
  activeAgentId: null,
  switchAgent: () => {},
  highlightElement: () => {},
  scrollToElement: () => {},
  helpComponents: [],
  faqs: [],
  searchHelp: async () => [],
  getComponentHelp: async () => null,
  getFAQ: async () => null,
  getLatestVersion: async () => null,
  startTour: () => {},
  getTourProgress: async () => null,
  currentContext: { page: window.location?.pathname ?? '/' },
  setCurrentContext: () => {},
  trackHelpInteraction: async () => {},
  submitFeedback: async () => {},
};

// =====================================================
// Hook to Use Help Context
// =====================================================

export function useHelp(): HelpContextValue {
  const context = useContext(HelpContext);
  return context ?? defaultHelpContextValue;
}

// =====================================================
// Export Context for Testing
// =====================================================

export { HelpContext };

